import React, { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { LabService } from "../../services/api/LaborderService/lab.service";
import BasicInfoSection from "./BasicInfoSection";
import PrecautionSection from "./PrecautionSection";
import VariantSection from "./VariantSection";
import {
  LabTest,
  LabTestCategory,
  Precaution,
  Variant,
  SelectedGroupTests,
  LabTestType,
} from "../../services/api/LaborderService/types";
import toast from "react-hot-toast";
interface AddLabtestFormProps {
  onSuccess: () => void;
  onClose: () => void;
  selectedLabTest: number | null;
  activeTab: "head" | "single" | "healthPackage" | "profile";
}
const AddLabTestForm: React.FC<AddLabtestFormProps> = ({
  onSuccess,
  onClose,
  selectedLabTest,
  activeTab,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<LabTest>({
    name: "",
    description: "",
    otherNames: "",
    price: "",
    offerPrice: "",
    discount: "",
    reportTime: "",
    ageLimitMin: "",
    ageLimitMax: "",
    gender: "",
    sampleType: "",
    fasting: false,
    showOnHome: false,
    isHealthPackage: false,
    icon: "",
    labTestCategory: "",
  });

  const [precautions, setPrecautions] = useState<Precaution[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedGroupTests, setSelectedGroupTests] = useState<
    SelectedGroupTests[]
  >([]);
  const [selectedSingleTests, setSelectedSingleTests] = useState<LabTestType[]>(
    [],
  );

  const handleInputChange = (field: keyof LabTest, value: string | boolean) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Auto-calculate Price Logic
      if (field === "price" || field === "discount") {
        // If Original Price or Discount changes -> Calc Offer Price
        // Offer = Original * (1 - Discount/100)
        const original = Number(field === "price" ? value : prev.price);
        const disc = Number(field === "discount" ? value : prev.discount);

        if (!isNaN(original) && !isNaN(disc)) {
          const offer = original - (original * disc) / 100;
          newData.offerPrice = offer.toFixed(2);
        }
      } else if (field === "offerPrice") {
        // If Offer Price changes -> Calc Discount
        // Discount = ((Original - Offer) / Original) * 100
        const original = Number(prev.price);
        const offer = Number(value);

        if (!isNaN(original) && original > 0 && !isNaN(offer)) {
          const disc = ((original - offer) / original) * 100;
          newData.discount = disc.toFixed(2);
        }
      }

      return newData;
    });
  };

  useEffect(() => {
    const fetchDetails = async () => {
      if (selectedLabTest) {
        try {
          const response =
            await LabService.getLabtestDetailsById(selectedLabTest);
          const labTestDetails = response;
          console.log("Fetched Details:", labTestDetails);

          setFormData({
            name: labTestDetails.name,
            description: labTestDetails.description,
            otherNames: labTestDetails.otherNames,
            price: labTestDetails.originalPrice
              ? labTestDetails.originalPrice.toString()
              : "",
            offerPrice: labTestDetails.offerPrice
              ? labTestDetails.offerPrice.toString()
              : "",
            discount: labTestDetails.discountPercentage
              ? labTestDetails.discountPercentage.toString()
              : "",
            reportTime: labTestDetails.reportTime,
            ageLimitMin: labTestDetails.ageLimitMin
              ? labTestDetails.ageLimitMin.toString()
              : "",
            ageLimitMax: labTestDetails.ageLimitMax
              ? labTestDetails.ageLimitMax.toString()
              : "",
            gender: labTestDetails.gender,
            sampleType: labTestDetails.sampleType,
            fasting: labTestDetails.fastingRequired,
            showOnHome: labTestDetails.isPublished,
            isHealthPackage: labTestDetails.isHealthPackage,
            labTestCategory: labTestDetails.labTestCategory,
            icon: "",
          });

          const prepDetails = (labTestDetails as any).preparationDetails || [];
          setPrecautions(
            prepDetails.map((pre: any) => ({
              id: Math.floor(Math.random() * 1000000).toString(),
              description: pre.description,
            })),
          );

          const allIncluded = labTestDetails.includedTests || [];

          const singles = allIncluded.filter((t: any) => t.type === "single");
          const groups = allIncluded.filter(
            (t: any) => t.type === "panel" || t.type === "profile",
          );

          setSelectedSingleTests(
            singles.map((test: any) => ({
              id: test.id,
              name: test.name,
              description: test.description,
              type: test.type,
            })),
          );

          setSelectedGroupTests(
            groups.map((test: any) => {
              let children: any[] = [];
              if (test.type === "panel") {
                children = test.singles || [];
                children = children.map((c: any) => ({ ...c, type: "single" }));
              } else if (test.type === "profile") {
                children = test.includedTests || [];
                children = children.map((c: any) => ({ ...c, type: "panel" }));
              }

              return {
                id: test.id,
                name: test.name,
                description: test.description,
                type: test.type,
                includedTestsCount: children.length.toString(),
                includedTests: children,
              };
            }),
          );

          const groupVariants: Variant[] = groups.map((group: any) => {
            const count =
              group.type === "panel"
                ? group.singles?.length || 0
                : group.includedTests?.length || 0;
            return {
              id: Math.floor(Math.random() * 1000000),
              testId: group.id,
              type: group.type,
              name: group.name,
              description: group.description,
              includedTests: count,
            };
          });

          const singleVariants: Variant[] = singles.map((test: any) => ({
            id: Math.floor(Math.random() * 1000000),
            testId: test.id,
            type: "single",
            name: test.name,
            description: test.description,
          }));

          setVariants([...groupVariants, ...singleVariants]);
          setPrecautions(
            labTestDetails.precautions.map((pre: any) => ({
              id: pre.id,
              description: pre.description,
            })),
          );
        } catch (error) {
          console.error("Error fetching lab test details:", error);
          toast.error("Failed to fetch lab test details");
        }
      }
    };

    fetchDetails();
  }, [selectedLabTest]);

  const handleVerifyForm = () => {
    if (
      formData.name &&
      formData.description &&
      formData.price &&
      formData.offerPrice &&
      formData.discount &&
      formData.reportTime &&
      formData.gender &&
      formData.sampleType &&
      selectedImage &&
      precautions.length > 0 &&
      variants.length > 0
    ) {
      return true;
    }

    if (!formData.name) return (toast.error("Name is required"), false);
    if (!formData.description)
      return (toast.error("Description is required"), false);
    if (!formData.price) return (toast.error("Price is required"), false);
    if (!formData.offerPrice)
      return (toast.error("Offer Price is required"), false);
    if (!formData.discount) return (toast.error("Discount is required"), false);
    if (!formData.reportTime)
      return (toast.error("Report Time is required"), false);
    if (!formData.gender) return (toast.error("Gender is required"), false);
    if (!formData.sampleType)
      return (toast.error("Sample Type is required"), false);
    if (activeTab !== "single" && variants.length < 1)
      return (toast.error("Variants are required"), false);

    return true;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    console.log(formData);
    e.preventDefault();
    const verifyForm = handleVerifyForm();
    if (!verifyForm) {
      return;
    }
    const isHealthPackage = activeTab === "healthPackage";

    // Constructing the Payload Object for JSON
    const payload: any = {
      name: formData.name,
      description: formData.description,
      otherNames: formData.otherNames,
      originalPrice: Number(formData.price),
      offerPrice: Number(formData.offerPrice),
      discountPercentage: Number(formData.discount),
      reportTime: formData.reportTime,
      ageLimitMin: Number(formData.ageLimitMin),
      ageLimitMax: Number(formData.ageLimitMax),
      gender: formData.gender,
      sampleType: formData.sampleType,
      fastingRequired: formData.fasting === true,
      isPublished: formData.showOnHome === true,
      isHealthPackage: isHealthPackage,
      labTestCategory: formData.labTestCategory,
      preparationDetails: [],
      includedTestsArray: [],
      includedTests: "0",
      type: "single",
    };

    // Condition 2: if original price and offer price is zero ,then set the is published as false , else true
    if (payload.originalPrice === 0 && payload.offerPrice === 0) {
      payload.isPublished = false;
    } else {
      payload.isPublished = true;
    }

    if (activeTab === "healthPackage") {
      payload.type = "health_package";
    } else if (activeTab === "head") {
      payload.type = "panel";
    } else if (activeTab === "profile") {
      payload.type = "profile";
    } else {
      payload.type = "single";
    }

    if (precautions.length > 0) {
      payload.preparationDetails = precautions.map((pre) => ({
        description: pre.description, // User request sample only shows description
      }));
    }

    // Variants handling
    let includedTestsPayload: any[] = [];
    if (variants.length > 0) {
      if (selectedSingleTests.length > 0) {
        const singles = selectedSingleTests.map((st) => ({
          includedType: "single",
          singleTestId: st.id,
          groupTestId: null,
          profileTestId: null,
          labtestId: selectedLabTest || undefined,
        }));
        includedTestsPayload = [...includedTestsPayload, ...singles];
      }

      if (selectedGroupTests.length > 0) {
        selectedGroupTests.forEach((group) => {
          group.includedTests.forEach((item) => {
            if (item.type === "single" || !item.type) {
              includedTestsPayload.push({
                includedType: "panel",
                singleTestId: item.id,
                groupTestId: group.id,
                profileTestId: null,
                labtestId: selectedLabTest || undefined,
              });
            } else if (item.type === "panel") {
              const panelItem = item as any;
              const panelSingles = panelItem.singles || [];
              panelSingles.forEach((s: any) => {
                includedTestsPayload.push({
                  includedType: "profile",
                  singleTestId: s.id,
                  groupTestId: panelItem.id,
                  profileTestId: group.id,
                  labtestId: selectedLabTest || undefined,
                });
              });
            }
          });
        });
      }

      payload.includedTestsArray = includedTestsPayload;
      payload.includedTests = includedTestsPayload.length.toString(); // Count total single tests
    }

    if (activeTab === "single") {
      payload.includedTests = "0"; // Or "1" if it counts itself? Usually 0 included for single.
    }

    try {
      console.log(payload);
      let response;
      if (selectedLabTest) {
        // Update Mode
        response = await LabService.updateLabTest(selectedLabTest, payload);
        toast.success("Lab test updated successfully");
      } else {
        // Create Mode
        response = await LabService.addLabtest(payload);
        toast.success("Lab test added successfully");
      }

      if (response) {
        onSuccess();
      }
    } catch (error) {
      toast.error("Failed to save lab test");
    }
  };

  const addText = selectedLabTest ? "UPDATE" : "ADD NEW";
  const headerText =
    activeTab === "healthPackage"
      ? `${addText} HEALTH PACKAGE`
      : activeTab === "head"
        ? `${addText} HEAD`
        : activeTab === "profile"
          ? `${addText} PROFILE`
          : `${addText} SINGLE TEST`;

  return (
    <div className="min-h-screen bg-gray-50 p-2 w-full">
      <div className="flex flex-row gap-2 pb-2 items-center">
        <button onClick={onClose}>
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-xl font-semibold text-green-600 flex items-center">
          {headerText}
        </h2>
      </div>

      <div className="bg-white rounded-lg p-4 border-2 border-gray-400/30 shadow-sm">
        <div className="flex flex-row gap-6 mb-6 w-full">
          <BasicInfoSection
            formData={formData}
            onInputChange={handleInputChange}
          />
        </div>

        <PrecautionSection
          precautions={precautions}
          setPrecautions={setPrecautions}
        />

        {activeTab !== "single" && (
          <VariantSection
            variants={variants}
            setVariants={setVariants}
            selectedGroupTests={selectedGroupTests}
            setSelectedGroupTests={setSelectedGroupTests}
            selectedSingleTests={selectedSingleTests}
            setSelectedSingleTests={setSelectedSingleTests}
            parentType={activeTab}
          />
        )}

        <div className="flex gap-3">
          <button
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            onClick={handleFormSubmit}
          >
            Save
          </button>
          <button className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddLabTestForm;
